import { useState, useEffect, useCallback, useRef } from 'react';
import { AnalyticsConfig, FilterConfig } from '../types/analytics.types';
import { FilterHistoryStorage } from '../services/filter-history.storage';

/**
 * Represents a saved filter state with metadata
 */
export interface FilterState {
  id: string;
  timestamp: Date;
  filters: Record<string, any>;
  summary: string;
}

/**
 * Result interface for the useFilterHistory hook
 */
export interface UseFilterHistoryResult {
  // Recent filters list (last 10 states)
  recentFilters: FilterState[];
  
  // Add current filter state to history
  addFilterState: (filters: Record<string, any>) => void;
  
  // Undo/Redo capabilities
  canUndo: boolean;
  canRedo: boolean;
  undo: () => Record<string, any> | null;
  redo: () => Record<string, any> | null;
  
  // Apply a saved filter state from recent list
  applyRecentFilter: (filterId: string) => Record<string, any> | null;
  
  // Clear all history
  clearHistory: () => void;
  
  // Delete specific filter states by ID
  deleteFilters: (filterIds: string[]) => void;
}

/**
 * Generate a human-readable summary of filter values
 */
const generateFilterSummary = (
  filters: Record<string, any>,
  filterConfigs: FilterConfig[]
): string => {
  const summaryParts: string[] = [];
  
  Object.keys(filters).forEach(filterId => {
    const value = filters[filterId];
    const filterConfig = filterConfigs.find(f => f.filterId === filterId);
    
    if (!filterConfig || value === null || value === undefined) return;
    
    const label = filterConfig.label || filterId;
    
    switch (filterConfig.type) {
      case 'date-range':
        if (value?.start && value?.end) {
          const startDate = new Date(value.start);
          const endDate = new Date(value.end);
          const formatDate = (date: Date) => {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
          };
          summaryParts.push(`${formatDate(startDate)} - ${formatDate(endDate)}`);
        }
        break;
      
      case 'multi-select':
        if (Array.isArray(value) && value.length > 0) {
          const totalOptions = filterConfig.options?.length || 0;
          if (value.length === totalOptions) {
            summaryParts.push(`All ${label}`);
          } else {
            summaryParts.push(`${value.length} ${label}`);
          }
        }
        break;
      
      case 'select':
        if (value) {
          const option = filterConfig.options?.find(opt => opt.value === value);
          summaryParts.push(`${label}: ${option?.label || value}`);
        }
        break;
      
      case 'number-range':
        if (value?.min !== undefined || value?.max !== undefined) {
          const min = value.min ?? '...';
          const max = value.max ?? '...';
          summaryParts.push(`${label}: ${min} - ${max}`);
        }
        break;
      
      case 'checkbox':
        if (value === true) {
          summaryParts.push(label);
        }
        break;
      
      case 'search':
        if (value && value.trim() !== '') {
          summaryParts.push(`Search: "${value}"`);
        }
        break;
    }
  });
  
  return summaryParts.length > 0 ? summaryParts.join(', ') : 'Default Filters';
};

/**
 * Deep equality check for filter objects
 */
const areFiltersEqual = (filters1: Record<string, any>, filters2: Record<string, any>): boolean => {
  const keys1 = Object.keys(filters1);
  const keys2 = Object.keys(filters2);
  
  if (keys1.length !== keys2.length) return false;
  
  return keys1.every(key => {
    const val1 = filters1[key];
    const val2 = filters2[key];
    
    // Handle null/undefined
    if (val1 === val2) return true;
    if (val1 === null || val1 === undefined || val2 === null || val2 === undefined) return false;
    
    // Handle arrays (multi-select)
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      return val1.every((v, i) => v === val2[i]);
    }
    
    // Handle objects (date-range, number-range)
    if (typeof val1 === 'object' && typeof val2 === 'object') {
      return JSON.stringify(val1) === JSON.stringify(val2);
    }
    
    return val1 === val2;
  });
};

/**
 * Custom hook for managing filter history with undo/redo capabilities
 * 
 * @param currentFilters - Current filter values
 * @param config - Analytics dashboard configuration
 * @param maxHistory - Maximum number of states to keep (default: 10)
 * @param userId - Optional user ID for backend sync
 */
export const useFilterHistory = (
  currentFilters: Record<string, any>,
  config: AnalyticsConfig,
  maxHistory: number = 10,
  userId?: string
): UseFilterHistoryResult => {
  // History stack for undo/redo
  const [historyStack, setHistoryStack] = useState<FilterState[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  
  // Recent filters list (shown in UI)
  const [recentFilters, setRecentFilters] = useState<FilterState[]>([]);
  
  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track if we're applying a history state (to prevent recursion)
  const isApplyingHistoryRef = useRef(false);
  
  // Storage service
  const storageRef = useRef(new FilterHistoryStorage(config.id, userId));
  
  // Load history from storage on mount
  useEffect(() => {
    const loadHistory = async () => {
      const stored = await storageRef.current.loadFromLocal();
      if (stored.length > 0) {
        setRecentFilters(stored);
        setHistoryStack(stored);
        setCurrentIndex(stored.length - 1);
      }
    };
    
    loadHistory();
  }, []);
  
  // Save to storage whenever history changes
  useEffect(() => {
    if (recentFilters.length > 0) {
      storageRef.current.saveToLocal(recentFilters);
      
      // Optionally sync to backend if user is signed in
      if (userId) {
        storageRef.current.syncToBackend?.(userId, recentFilters).catch(err => {
          console.warn('Failed to sync filter history to backend:', err);
        });
      }
    }
  }, [recentFilters, userId]);
  
  /**
   * Add a new filter state to history
   * Debounced by 500ms to avoid adding on every keystroke
   */
  const addFilterState = useCallback((filters: Record<string, any>) => {
    // Skip if we're applying a history state
    if (isApplyingHistoryRef.current) return;
    
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce the addition
    debounceTimerRef.current = setTimeout(() => {
      setHistoryStack(prevStack => {
        // Check if filters are different from current state
        if (currentIndex >= 0 && currentIndex < prevStack.length) {
          const currentState = prevStack[currentIndex];
          if (areFiltersEqual(currentState.filters, filters)) {
            return prevStack; // No change, don't add to history
          }
        }
        
        // Create new filter state
        const newState: FilterState = {
          id: `filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
          filters: { ...filters },
          summary: generateFilterSummary(filters, config.filters || [])
        };
        
        // Remove any states after current index (redo states)
        const newStack = prevStack.slice(0, currentIndex + 1);
        
        // Add new state
        newStack.push(newState);
        
        // Keep only last maxHistory states
        const trimmedStack = newStack.slice(-maxHistory);
        
        // Update current index
        setCurrentIndex(trimmedStack.length - 1);
        
        // Update recent filters list
        setRecentFilters(trimmedStack);
        
        return trimmedStack;
      });
    }, 500);
  }, [currentIndex, config.filters, maxHistory]);
  
  /**
   * Undo to previous filter state
   */
  const undo = useCallback((): Record<string, any> | null => {
    if (currentIndex <= 0) return null;
    
    const newIndex = currentIndex - 1;
    const prevState = historyStack[newIndex];
    
    if (prevState) {
      setCurrentIndex(newIndex);
      isApplyingHistoryRef.current = true;
      
      // Reset flag after a short delay
      setTimeout(() => {
        isApplyingHistoryRef.current = false;
      }, 100);
      
      return prevState.filters;
    }
    
    return null;
  }, [currentIndex, historyStack]);
  
  /**
   * Redo to next filter state
   */
  const redo = useCallback((): Record<string, any> | null => {
    if (currentIndex >= historyStack.length - 1) return null;
    
    const newIndex = currentIndex + 1;
    const nextState = historyStack[newIndex];
    
    if (nextState) {
      setCurrentIndex(newIndex);
      isApplyingHistoryRef.current = true;
      
      // Reset flag after a short delay
      setTimeout(() => {
        isApplyingHistoryRef.current = false;
      }, 100);
      
      return nextState.filters;
    }
    
    return null;
  }, [currentIndex, historyStack]);
  
  /**
   * Apply a filter state from recent filters list
   */
  const applyRecentFilter = useCallback((filterId: string): Record<string, any> | null => {
    const filterState = recentFilters.find(f => f.id === filterId);
    
    if (filterState) {
      // Find index in history stack
      const index = historyStack.findIndex(f => f.id === filterId);
      if (index >= 0) {
        setCurrentIndex(index);
      }
      
      isApplyingHistoryRef.current = true;
      
      // Reset flag after a short delay
      setTimeout(() => {
        isApplyingHistoryRef.current = false;
      }, 100);
      
      return filterState.filters;
    }
    
    return null;
  }, [recentFilters, historyStack]);
  
  /**
   * Clear all history
   */
  const clearHistory = useCallback(() => {
    setHistoryStack([]);
    setRecentFilters([]);
    setCurrentIndex(-1);
    storageRef.current.clearStorage();
  }, []);
  
  /**
   * Delete specific filter states by ID
   */
  const deleteFilters = useCallback((filterIds: string[]) => {
    const filterIdSet = new Set(filterIds);
    
    setHistoryStack(prevStack => {
      const newStack = prevStack.filter(f => !filterIdSet.has(f.id));
      
      // Adjust current index if needed
      if (currentIndex >= 0 && currentIndex < prevStack.length) {
        const currentState = prevStack[currentIndex];
        if (filterIdSet.has(currentState.id)) {
          // Current state was deleted, reset to last remaining state
          setCurrentIndex(newStack.length - 1);
        } else {
          // Find new index of current state
          const newIndex = newStack.findIndex(f => f.id === currentState.id);
          setCurrentIndex(newIndex);
        }
      } else {
        setCurrentIndex(newStack.length - 1);
      }
      
      return newStack;
    });
    
    setRecentFilters(prevFilters => {
      const newFilters = prevFilters.filter(f => !filterIdSet.has(f.id));
      
      // Update storage
      if (newFilters.length > 0) {
        storageRef.current.saveToLocal(newFilters);
      } else {
        storageRef.current.clearStorage();
      }
      
      return newFilters;
    });
  }, [currentIndex]);
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  
  return {
    recentFilters,
    addFilterState,
    canUndo: currentIndex > 0,
    canRedo: currentIndex < historyStack.length - 1,
    undo,
    redo,
    applyRecentFilter,
    clearHistory,
    deleteFilters
  };
};

